import React from 'react';
import { Banknote } from 'lucide-react';

export const DENOMINATIONS = [
  { value: 2000, label: '₹2,000', type: 'note' },
  { value: 500, label: '₹500', type: 'note' },
  { value: 200, label: '₹200', type: 'note' },
  { value: 100, label: '₹100', type: 'note' },
  { value: 50, label: '₹50', type: 'note' },
  { value: 20, label: '₹20', type: 'note' },
  { value: 10, label: '₹10', type: 'note' },
  { value: 5, label: '₹5', type: 'coin' },
  { value: 2, label: '₹2', type: 'coin' },
  { value: 1, label: '₹1', type: 'coin' },
];

// Colors inspired by Indian Rupee currency
export const DENOM_COLORS = {
  2000: { ticket: 'bg-pink-100 text-pink-800', row: 'bg-pink-50/50' },
  500:  { ticket: 'bg-stone-100 text-stone-700', row: 'bg-stone-50/60' },
  200:  { ticket: 'bg-yellow-100 text-yellow-800', row: 'bg-yellow-50/50' },
  100:  { ticket: 'bg-violet-100 text-violet-800', row: 'bg-violet-50/50' },
  50:   { ticket: 'bg-sky-100 text-sky-800', row: 'bg-sky-50/50' },
  20:   { ticket: 'bg-lime-100 text-lime-700', row: 'bg-lime-50/50' },
  10:   { ticket: 'bg-orange-100 text-orange-800', row: 'bg-orange-50/50' },
  5:    { ticket: 'bg-amber-100 text-amber-800', row: 'bg-amber-50/40' },
  2:    { ticket: 'bg-slate-200 text-slate-700', row: 'bg-slate-100/50' },
  1:    { ticket: 'bg-zinc-200 text-zinc-700', row: 'bg-zinc-100/50' },
};

// Hex lookup for the radial-gradient ticket shape
export const DENOM_HEX = {
  2000: '#fce7f3', 500: '#f5f5f4', 200: '#fef9c3', 100: '#ede9fe',
  50: '#e0f2fe', 20: '#ecfccb', 10: '#ffedd5',
  5: '#fef3c7', 2: '#e2e8f0', 1: '#e4e4e7',
};

const fmt = (val) =>
  Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * DenominationPanel — cash counting by denomination
 *
 * @param {Object} counts - { "2000": 2, "500": 5, ... } denomination counts
 * @param {Function} onChange - called with updated counts object
 * @param {number} expectedCash - expected cash amount for comparison
 */
export default function DenominationPanel({ counts, onChange, expectedCash }) {
  const handleCountChange = (denomination, value) => {
    const count = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
    onChange({ ...counts, [denomination]: count });
  };

  const total = DENOMINATIONS.reduce(
    (sum, d) => sum + d.value * (counts[d.value] || 0),
    0
  );

  const diff = expectedCash != null ? total - expectedCash : null;
  const notes = DENOMINATIONS.filter((d) => d.type === 'note');
  const coins = DENOMINATIONS.filter((d) => d.type === 'coin');

  const renderRow = (d) => {
    const count = counts[d.value] || 0;
    const amount = d.value * count;
    const colors = DENOM_COLORS[d.value];
    const isNote = d.type === 'note';

    return (
      <div
        key={d.value}
        className={`grid grid-cols-[100px_1fr_80px] gap-2 items-center px-2 py-1.5 rounded-lg transition ${
          count > 0 ? colors.row : 'hover:bg-slate-50'
        }`}
      >
        {/* Denomination badge */}
        {isNote ? (
          <NoteTicket value={d.value} label={d.label} ticketClass={colors.ticket} />
        ) : (
          <div className="flex justify-center">
            <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 border-current/20 ${colors.ticket}`}>
              <span className="text-[11px] font-bold">{d.label}</span>
            </div>
          </div>
        )}

        {/* Count controls */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => handleCountChange(d.value, Math.max(0, count - 1))}
            className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm transition flex items-center justify-center"
            tabIndex={-1}
          >
            -
          </button>
          <input
            type="number"
            value={count || ''}
            onChange={(e) => handleCountChange(d.value, e.target.value)}
            min="0"
            className="w-14 mx-1.5 text-center text-sm font-semibold text-slate-700 border border-slate-200 rounded-md py-1 focus:outline-none focus:ring-1 focus:ring-teal-400 focus:border-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder="0"
          />
          <button
            type="button"
            onClick={() => handleCountChange(d.value, count + 1)}
            className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm transition flex items-center justify-center"
            tabIndex={-1}
          >
            +
          </button>
        </div>

        {/* Amount */}
        <span
          className={`text-sm text-right tabular-nums ${
            count > 0 ? 'font-semibold text-slate-800' : 'text-slate-300'
          }`}
        >
          ₹{fmt(amount)}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Column header */}
      <div className="grid grid-cols-[100px_1fr_80px] gap-2 px-2 pb-0.5">
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Denom</span>
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider text-center">Count</span>
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider text-right">Amount</span>
      </div>

      {/* Notes */}
      <div className="space-y-0.5">{notes.map(renderRow)}</div>

      {/* Coins */}
      <div className="border-t border-slate-200 pt-2">
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider px-2 mb-1">
          Coins
        </p>
        <div className="space-y-0.5">{coins.map(renderRow)}</div>
      </div>

      {/* Total + Difference */}
      <div className="border-t border-slate-300 pt-3 space-y-1.5">
        <div className="flex justify-between items-center px-2">
          <span className="text-sm font-bold text-slate-700">Denomination Total</span>
          <span className="text-lg font-bold text-slate-900 tabular-nums">₹{fmt(total)}</span>
        </div>
        {expectedCash != null && (
          <div className="flex justify-between items-center px-2">
            <span className="text-xs text-slate-500">Expected Cash</span>
            <span className="text-sm font-semibold text-slate-600 tabular-nums">₹{fmt(expectedCash)}</span>
          </div>
        )}
        {diff !== null && total > 0 && (
          <div className="flex justify-between items-center px-2">
            <span className="text-xs text-slate-500">Difference</span>
            <span
              className={`text-sm font-bold tabular-nums ${
                diff === 0
                  ? 'text-emerald-600'
                  : diff < 0
                  ? 'text-red-600'
                  : 'text-amber-600'
              }`}
            >
              {diff >= 0 ? '+' : ''}₹{fmt(diff)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * NoteTicket — renders a banknote-shaped ticket badge with side notch cutouts.
 * Uses radial-gradient to punch transparent semicircles on left & right edges,
 * creating the classic ticket/coupon silhouette.
 */
export function NoteTicket({ value, label, ticketClass }) {
  const hex = DENOM_HEX[value] || '#f1f5f9';

  return (
    <div
      className={`h-[34px] flex items-center justify-center gap-1.5 rounded-[3px] ${ticketClass}`}
      style={{
        background: `
          radial-gradient(circle at 0    50%, transparent 5px, ${hex} 5.5px),
          radial-gradient(circle at 100% 50%, transparent 5px, ${hex} 5.5px)
        `,
        backgroundSize: '51% 100%',
        backgroundPosition: 'left, right',
        backgroundRepeat: 'no-repeat',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.06)',
      }}
    >
      <Banknote className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />
      <span className="text-[11px] font-extrabold tracking-wider">{label}</span>
    </div>
  );
}

/** Compute total from denomination counts object */
export function getDenominationTotal(counts) {
  if (!counts) return 0;
  return DENOMINATIONS.reduce(
    (sum, d) => sum + d.value * (counts[d.value] || 0),
    0
  );
}

/** Serialize denomination counts for API (JSON string) */
export function serializeDenomination(counts) {
  if (!counts) return null;
  const entries = DENOMINATIONS
    .filter((d) => (counts[d.value] || 0) > 0)
    .map((d) => ({ value: d.value, count: counts[d.value] }));
  if (entries.length === 0) return null;
  return JSON.stringify(entries);
}

/** Deserialize denomination JSON string to counts object */
export function deserializeDenomination(json) {
  if (!json) return {};
  try {
    const entries = typeof json === 'string' ? JSON.parse(json) : json;
    const counts = {};
    if (Array.isArray(entries)) {
      entries.forEach((e) => {
        counts[e.value] = e.count;
      });
    }
    return counts;
  } catch {
    return {};
  }
}

/**
 * DenominationView — read-only display of saved denomination breakdown.
 *
 * @param {string} denomination - JSON string "[{value:2000,count:2},...]"
 */
export function DenominationView({ denomination }) {
  const counts = deserializeDenomination(denomination);
  const entries = DENOMINATIONS.filter((d) => (counts[d.value] || 0) > 0);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-slate-400 text-center py-4">No denomination data</p>
    );
  }

  const total = entries.reduce((sum, d) => sum + d.value * (counts[d.value] || 0), 0);

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="grid grid-cols-[100px_1fr_80px] gap-2 px-2 pb-1">
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Denom</span>
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider text-center">Count</span>
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider text-right">Amount</span>
      </div>

      {/* Rows */}
      {entries.map((d) => {
        const count = counts[d.value] || 0;
        const amount = d.value * count;
        const colors = DENOM_COLORS[d.value];
        const isNote = d.type === 'note';

        return (
          <div
            key={d.value}
            className={`grid grid-cols-[100px_1fr_80px] gap-2 items-center px-2 py-1.5 rounded-lg ${colors.row}`}
          >
            {isNote ? (
              <NoteTicket value={d.value} label={d.label} ticketClass={colors.ticket} />
            ) : (
              <div className="flex justify-center">
                <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 border-current/20 ${colors.ticket}`}>
                  <span className="text-[11px] font-bold">{d.label}</span>
                </div>
              </div>
            )}
            <span className="text-sm font-semibold text-slate-700 text-center tabular-nums">
              {count}
            </span>
            <span className="text-sm font-semibold text-slate-800 text-right tabular-nums">
              ₹{fmt(amount)}
            </span>
          </div>
        );
      })}

      {/* Total */}
      <div className="border-t border-slate-300 pt-3 mt-2">
        <div className="flex justify-between items-center px-2">
          <span className="text-sm font-bold text-slate-700">Total</span>
          <span className="text-lg font-bold text-slate-900 tabular-nums">₹{fmt(total)}</span>
        </div>
      </div>
    </div>
  );
}
